import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/api";
import { Btn, Card, StatusBadge, Tag, Spinner } from "@/components/ui-kit";
import { EmptyState } from "@/components/dashboard-bits";
import { Wallet, MapPin, Users, Trash2, PlusCircle, Pencil, Lock, Unlock, Percent } from "lucide-react";
import { toast } from "sonner";
import { AffiliateBadge } from "@/components/AffiliateCard";
import { isAffiliate, formatCommission } from "@/lib/affiliate";

export default function MyCampaigns() {
  const [campaigns, setCampaigns] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/campaigns/mine").then((r) => setCampaigns(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    const noun = isAffiliate(c) ? "Affiliate-ohjelma" : "Kampanja";
    if (!window.confirm(`Poistetaanko ${isAffiliate(c) ? "affiliate-ohjelma" : "kampanja"} pysyvästi?`)) return;
    try {
      await api.delete(`/campaigns/${c.campaign_id}`);
      toast.success(`${noun} poistettu`);
      load();
    } catch { toast.error("Poisto epäonnistui"); }
  };

  const toggleStatus = async (c) => {
    const next = c.status === "open" ? "closed" : "open";
    try {
      await api.put(`/campaigns/${c.campaign_id}/status`, { status: next });
      const noun = isAffiliate(c) ? "Affiliate-ohjelma" : "Kampanja";
      toast.success(`${noun} ${next === "open" ? "avattu" : "suljettu"}`);
      load();
    } catch { toast.error("Tilan vaihto epäonnistui"); }
  };

  if (!campaigns) return <Spinner />;

  return (
    <div data-testid="my-campaigns-page">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-semibold tracking-display text-[#292827]">Omat kampanjat</h3>
        <div className="flex flex-wrap justify-end gap-2">
          <Btn to="/dashboard/julkaise" variant="sm"><PlusCircle size={16} /> Julkaise</Btn>
          <Btn to="/dashboard/julkaise-affiliate" variant="sm" data-testid="my-campaigns-publish-affiliate"><Percent size={16} /> Affiliate-ohjelma</Btn>
        </div>
      </div>
      {campaigns.length === 0 ? (
        <EmptyState title="Ei kampanjoita vielä" subtitle="Julkaise ensimmäinen kampanjasi."
          action={<Btn to="/dashboard/julkaise" variant="primary"><PlusCircle size={16} /> Julkaise kampanja</Btn>} />
      ) : (
        <div className="space-y-4">
          {campaigns.map((c) => (
            <Card key={c.campaign_id} className="p-6" data-testid="my-campaign-card">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4 min-w-0">
                  {c.images?.[0] && (
                    <div className="h-16 w-16 rounded-xl overflow-hidden bg-[#E9EAEC] border border-[#E6E1D9] shrink-0">
                      <img src={fileUrl(c.images[0])} alt="" className="h-full w-full object-cover" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      {isAffiliate(c) && <AffiliateBadge c={c} />}
                      <h4 className="text-[17px] font-semibold text-[#292827] tracking-tightish truncate">{c.title}</h4>
                      <StatusBadge status={c.status} />
                    </div>
                    <p className="mt-1 text-sm text-[#6E675C] line-clamp-1">{c.description}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[#6E675C]">
                      {(c.categories?.length ? c.categories : [c.category]).filter(Boolean).slice(0, 2).map((t) => <Tag key={t}>{t}</Tag>)}
                      {isAffiliate(c) ? (
                        <span className="flex items-center gap-1.5" data-testid="my-affiliate-commission"><Percent size={14} className="text-[#292827]" /> {formatCommission(c.commission)} provisio</span>
                      ) : (<>
                        <span className="flex items-center gap-1.5"><Wallet size={14} className="text-[#292827]" /> {c.budget || (c.budget_from && c.budget_to ? `${c.budget_from}–${c.budget_to} €` : "—")}</span>
                        <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[#292827]" /> {c.location || c.work_location || "—"}</span>
                      </>)}
                      <span className="flex items-center gap-1.5"><Users size={14} className="text-[#292827]" /> {c.application_count} hakemusta</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => navigate(`/dashboard/${isAffiliate(c) ? "julkaise-affiliate" : "julkaise"}?edit=${c.campaign_id}`)} data-testid="edit-campaign"
                    className="rounded-xl px-3 py-2.5 text-sm font-medium text-[#292827] border border-[#E6E1D9] hover:border-[#292827] transition-colors inline-flex items-center gap-1.5">
                    <Pencil size={15} /> Muokkaa
                  </button>
                  <button onClick={() => toggleStatus(c)} data-testid="toggle-campaign"
                    className="rounded-xl px-3 py-2.5 text-sm font-medium text-[#292827] border border-[#E6E1D9] hover:border-[#292827] transition-colors inline-flex items-center gap-1.5">
                    {c.status === "open" ? <><Lock size={15} /> Sulje</> : <><Unlock size={15} /> Avaa</>}
                  </button>
                  <button onClick={() => remove(c)} data-testid="delete-campaign"
                    className="rounded-xl p-2.5 text-[#968F85] hover:text-red-500 hover:bg-red-50 transition-colors">
                    <Trash2 size={17} strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
